import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';

import { subscribeToUser } from '../../lib/realtime';
import { useSession } from '../../stores/session';
import { messages as dm } from './api';

/**
 * The envelope in the header, with an unread count.
 *
 * A sibling of the bell rather than an item in the account menu: an unread
 * message needs to be visible without opening anything, and a badge inside a
 * dropdown is a badge nobody sees.
 *
 * The count comes from the inbox endpoint, which computes it exactly from
 * `id > last_read_message_id` (§6.5) rather than from a maintained counter.
 */
export function MessagesIndicator() {
  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => dm.inbox(),
    enabled: user !== null,
    /*
     * A slow backstop.
     *
     * `message.new` arrives on the conversation's own channel, which this
     * component is not subscribed to - it would have to join every thread the
     * person is in. So the poll is what catches a message in a thread they do
     * not currently have open, and it is deliberately unhurried: an envelope
     * badge appearing a minute late is not a defect.
     *
     * The Phase 4 shape §8.1 anticipates is a `dm.badge` event on
     * `private-user.{id}`, at which point this drops to a backstop like the
     * bell's.
     */
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  /*
   * Also refetched whenever anything lands on the user's own channel.
   *
   * Not because notifications and messages are the same thing, but because the
   * connection is already open and an event is a cheap hint that the person is
   * being interacted with. Costs one request, and makes the common case - being
   * messaged while looking at the site - feel immediate.
   */
  useEffect(() => {
    if (!user) return;

    return subscribeToUser(user.id, () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    });
  }, [user, queryClient]);

  if (!user) return null;

  const unread = data?.unread_total ?? 0;

  return (
    <NavLink
      to="/messages"
      aria-label={unread > 0 ? `Messages, ${unread} unread` : 'Messages'}
      data-messages-indicator
      data-unread={unread}
      className={({ isActive }) =>
        `relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] transition-colors hover:bg-raised hover:text-ink ${
          isActive ? 'text-ink' : 'text-soft'
        }`
      }
    >
      <svg
        viewBox="0 0 20 20"
        className="h-[18px] w-[18px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
        <path d="M3 6l7 5 7-5" />
      </svg>

      {unread > 0 && (
        <span
          // The link's own label carries the number; announcing it twice is
          // noise.
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-[var(--radius-pill)] bg-hot px-1 text-[10px] font-semibold text-white"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </NavLink>
  );
}
